/**
 * The canonical list of pages, and how to fold a concrete URL onto one.
 *
 * WHY A LIST AND NOT JUST "WHATEVER WE SAW"
 *   The question is which pages are worth developing and which are ignored, and
 *   you cannot answer the second half from observed traffic alone: a page
 *   nobody has ever opened produces no rows, so it is invisible in a GROUP BY.
 *   Enumerating the routes means a page with zero views shows up AS zero, which
 *   is the finding.
 *
 * WHY NORMALISE
 *   /pokemon/25 and /pokemon/151 are the same page. Left raw, the Pokémon
 *   detail page would shatter into a thousand one-view rows and disappear from
 *   the ranking, while the pages with no parameters would look artificially
 *   dominant.
 */

export type PageArea = "core" | "dex" | "social" | "raids" | "tools" | "account" | "auth" | "admin";

export interface PageRoute {
  /** Wouter-style pattern; `:param` segments match anything. */
  pattern: string;
  label: string;
  area: PageArea;
  /**
   * Other URLs that render this SAME page, folded onto `pattern`.
   *
   * Not a nicety. A page reachable at two URLs and listed as two routes has
   * its traffic split down the middle, so it under-ranks every page it
   * actually beats — the same failure the `:param` folding above exists to
   * prevent, arrived at from the other direction.
   */
  aliases?: string[];
}

/**
 * Ordered MOST SPECIFIC FIRST. `/friends/:id/trades` has to be tested before
 * `/friends/:id`, or the more general pattern would swallow it and the two
 * pages would report as one.
 */
export const PAGE_ROUTES: PageRoute[] = [
  // Dex
  { pattern: "/pokemon/:dexNumber", label: "Pokémon detail", area: "dex" },
  { pattern: "/my-pokemon/:id", label: "My Pokémon record", area: "dex" },
  { pattern: "/my-pokemon", label: "My Pokémon", area: "dex" },
  { pattern: "/stardust-planner", label: "Stardust Planner", area: "dex" },
  { pattern: "/mega", label: "Mega Evolutions", area: "dex" },
  { pattern: "/naming", label: "IV naming", area: "dex" },
  { pattern: "/cleanup", label: "Box Cleanup", area: "dex" },
  { pattern: "/scan-history", label: "Scan history", area: "dex" },
  { pattern: "/walkthroughs", label: "Tutorials", area: "account" },
  { pattern: "/wish-list", label: "Wish list", area: "dex" },
  // Both render PokedexPage — one page, two URLs.
  { pattern: "/dex", label: "Pokédex", area: "dex", aliases: ["/pokedex"] },
  // Raids — specific paths before the /:id catch-all.
  { pattern: "/raids/passport", label: "Raid passport", area: "raids" },
  { pattern: "/raids/host", label: "Host a raid", area: "raids" },
  { pattern: "/raids/boss/:bossKey", label: "Raid boss", area: "raids" },
  { pattern: "/raids/:id", label: "Raid lobby", area: "raids" },
  { pattern: "/raids", label: "Raids", area: "raids" },
  // Social
  { pattern: "/friends/manage", label: "Manage friends", area: "social" },
  { pattern: "/friends/:id/compare", label: "Compare with friend", area: "social" },
  { pattern: "/friends/:id/trades", label: "Friend trades", area: "social" },
  { pattern: "/friends", label: "Friends", area: "social" },
  { pattern: "/trade", label: "Trade", area: "social" },
  // Tools
  // TEMPORARY review routes (2026-08-20) — see REVIEW_LINKS in MainMenu.tsx.
  { pattern: "/dex/:variant/:dexNumber/forms", label: "Forms sub-dex", area: "tools" },
  { pattern: "/review/features-map", label: "Features map", area: "tools" },
  { pattern: "/calendar", label: "Calendar", area: "tools" },
  { pattern: "/bonuses", label: "Bonuses", area: "tools" },
  { pattern: "/battle", label: "Battle hub", area: "tools" },
  { pattern: "/rocket", label: "Rocket", area: "tools" },
  { pattern: "/dynamax", label: "MAX Battles", area: "tools" },
  { pattern: "/showcases", label: "Showcases", area: "tools" },
  { pattern: "/power-scouter", label: "Power Scouter", area: "tools" },
  // Account
  { pattern: "/settings/billing", label: "Billing", area: "account" },
  { pattern: "/settings", label: "Settings", area: "account" },
  { pattern: "/portal/account", label: "Portal account", area: "account" },
  // The rest of the web portal. Product, not tooling: /portal/admin is ours
  // and collapses separately below.
  { pattern: "/portal/dex", label: "Portal records", area: "dex" },
  { pattern: "/portal/upload", label: "Portal CSV upload", area: "tools" },
  { pattern: "/portal", label: "Portal home", area: "account" },
  // Auth
  { pattern: "/login", label: "Login", area: "auth" },
  { pattern: "/signup", label: "Sign up", area: "auth" },
  { pattern: "/welcome", label: "Welcome", area: "auth" },
  // Each of these is registered twice in App.tsx — once bare and once under
  // /portal — pointing at the same component. Folded, or a password reset
  // reads as two half-used pages instead of one that works.
  {
    pattern: "/auth/verify-email",
    label: "Verify email",
    area: "auth",
    aliases: ["/portal/auth/verify-email"],
  },
  {
    pattern: "/auth/forgot-password",
    label: "Forgot password",
    area: "auth",
    aliases: ["/portal/auth/forgot-password"],
  },
  {
    pattern: "/auth/reset-password",
    label: "Reset password",
    area: "auth",
    aliases: ["/portal/auth/reset-password"],
  },
  // Core. Last, because "/" would otherwise need special-casing against
  // everything above it.
  { pattern: "/", label: "Profile (home)", area: "core" },
];

/**
 * Anything under one of these is us, not the product. Recorded separately.
 *
 * /admin/activity is why this is a list. It renders the same AdminActivity
 * page as /portal/admin/activity but sits outside the portal prefix, so a
 * single-prefix check dropped our own tooling into the product's "other"
 * bucket — inflating a number whose whole job is to be believable.
 */
const ADMIN_PREFIXES = ["/portal/admin", "/admin"];

/** Everything that is not a known product page or an admin page. */
export const OTHER_PAGE = "other";
export const ADMIN_PAGE = "/portal/admin/*";

function matches(pattern: string, path: string): boolean {
  const p = pattern.split("/").filter(Boolean);
  const s = path.split("/").filter(Boolean);
  if (p.length !== s.length) return false;
  return p.every((seg, i) => seg.startsWith(":") || seg === s[i]);
}

/**
 * Fold a concrete path onto a stable page key.
 *
 * Query strings and hashes are stripped, and a trailing slash is ignored, so
 * "/friends", "/friends/" and "/friends?tab=x" are one page rather than three.
 */
export function normalisePath(rawPath: string): string {
  const path = (rawPath.split("?")[0].split("#")[0] || "/").replace(/(.)\/+$/, "$1");
  if (path === "/" ) return "/";
  // Admin is collapsed to a single key on purpose: it is our tooling, and
  // twelve internal pages would otherwise crowd out the product in the ranking.
  if (ADMIN_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return ADMIN_PAGE;
  for (const r of PAGE_ROUTES) {
    // An alias resolves to its route's canonical pattern, so the two URLs
    // report as the one page they are.
    if (matches(r.pattern, path) || r.aliases?.some((a) => matches(a, path))) return r.pattern;
  }
  // Unknown but real: kept as one bucket rather than dropped, so a route added
  // without being listed here shows up as something to explain.
  return OTHER_PAGE;
}

export function labelForPage(key: string): string {
  if (key === ADMIN_PAGE) return "Admin portal (ours)";
  if (key === OTHER_PAGE) return "Other / unlisted";
  return PAGE_ROUTES.find((r) => r.pattern === key)?.label ?? key;
}

export function areaForPage(key: string): PageArea | "other" {
  if (key === ADMIN_PAGE) return "admin";
  return PAGE_ROUTES.find((r) => r.pattern === key)?.area ?? "other";
}

/**
 * Suppression rule for repeat views.
 *
 * THIS USED TO BE A 60s PER-PAGE COOLDOWN, AND THAT WAS WRONG.
 *   It was fine for ranking pages, but it silently destroys journeys: a trainer
 *   going Dex -> Pokémon -> Dex inside a minute had the second Dex dropped, so
 *   the recorded path was "Dex, Pokémon" and the Pokémon page appeared to be
 *   where they sat for the rest of the visit. Both the sequence and the dwell
 *   time were wrong, and in the same direction every time.
 *
 * THE RULE NOW: only suppress the SAME page arriving twice in a row.
 *   Going away and coming back is real behaviour and is exactly what journey
 *   analysis is for, so it is recorded. A page repeating with nothing in
 *   between is a re-render or a redirect, and is not.
 *
 *   The short floor is a backstop for a redirect LOOP (A->B->A->B in
 *   milliseconds), which consecutive-dedupe alone would happily record forever.
 */
export const PAGE_VIEW_MIN_GAP_MS = 2_000;

let lastPage: string | null = null;
let lastAt = 0;

export function shouldRecordView(key: string, now: number = Date.now()): boolean {
  if (key === lastPage) return false;
  if (now - lastAt < PAGE_VIEW_MIN_GAP_MS) return false;
  lastPage = key;
  lastAt = now;
  return true;
}

/** Test seam — the suppression state is module-level. */
export function resetViewThrottle(): void {
  lastPage = null;
  lastAt = 0;
}
